// mouseenter / mouseleave for a finger. Touch events only ever target the
// element the gesture started on, so anything that wants to know when a
// dragged finger passes over it registers here and is told from a single
// document-level touchmove listener.

type Handler = [HTMLElement, () => void];

const leaveHandlers: Handler[] = [];
const enterHandlers: Handler[] = [];

const register = (list: Handler[], el: HTMLElement, fn: () => void) => {
  const handler: Handler = [el, fn];
  list.push(handler);

  return function () {
    const index = list.indexOf(handler);

    if (index >= 0) {
      list.splice(index, 1);
    }
  };
};

const fire = (list: Handler[], el: HTMLElement) => {
  // Copied first: a handler may (un)register others while it runs.
  list.slice().forEach(([target, fn]) => {
    if (target === el) {
      fn();
    }
  });
};

export const onTouchEnter = (el: HTMLElement, fn: () => void) =>
  register(enterHandlers, el, fn);

export const onTouchLeave = (el: HTMLElement, fn: () => void) =>
  register(leaveHandlers, el, fn);

// The registered elements the finger is currently inside, ancestors included,
// the same set the mouse would count as hovered.
let hovered = new Set<HTMLElement>();

const hoveredAt = (x: number, y: number) => {
  const registered = new Set<Element>();
  leaveHandlers.forEach(([el]) => registered.add(el));
  enterHandlers.forEach(([el]) => registered.add(el));

  // The topmost registered element under the finger, looking through anything
  // unregistered that covers it, then everything registered above it in the
  // tree: the container a zone sits in is entered and left along with it.
  const top =
    document.elementsFromPoint(x, y).find((el) => registered.has(el)) ?? null;
  const next = new Set<HTMLElement>();

  for (let el: Element | null = top; el; el = el.parentElement) {
    if (registered.has(el)) {
      next.add(el as HTMLElement);
    }
  }

  return next;
};

document.addEventListener("touchmove", (e) => {
  const { clientX, clientY } = e.touches[0];
  const next = hoveredAt(clientX, clientY);

  // Leaves before enters, as the mouse does, so a zone handler that clears
  // state on leave cannot undo the enter of the zone the finger moved to.
  // Moving off every registered element leaves them all: nothing stays lit
  // once the finger is over empty space.
  hovered.forEach((el) => {
    if (!next.has(el)) {
      fire(leaveHandlers, el);
    }
  });
  next.forEach((el) => {
    if (!hovered.has(el)) {
      fire(enterHandlers, el);
    }
  });

  hovered = next;
});

// Letting go is not leaving (the mouse does not leave on mouseup either, and
// the release handlers still want the zone the finger was on). The next
// gesture starts with nothing hovered.
const reset = () => {
  hovered = new Set();
};
document.addEventListener("touchend", reset);
document.addEventListener("touchcancel", reset);
